import { createSlice } from '@reduxjs/toolkit';
import _orderBy from 'lodash/orderBy';

import songsDataRaw from 'data/songs.json';
import albumDataRaw from 'data/albums.json';

import concertsDataRaw from 'data/concerts.json';

import {
  StatisticState,
  Concert,
  Album,
  Song,
  MostPlayed,
  Years
} from 'interfaces';

const initialStateDefault: StatisticState = {
  songPlayed: null,
  albumPlayed: null,
  allShows: null,
  mostPlayedSongs: [],
  mostPlayedAlbums: [],
  bandStats: null,
  years: null,
  yearsFes: null
};

const albumsData: Album[] = albumDataRaw as unknown as Album[];
const songsData: Song[] = songsDataRaw as unknown as Song[];
const concertsData: Concert[] = concertsDataRaw as unknown as Concert[];

const statisticsSlice = createSlice({
  name: 'statistics',
  initialState: initialStateDefault,
  reducers: {
    getAllShows (state, action) {
      const { tourId, limitYear } = action.payload;

      const concerts = concertsData
        .filter((el) => tourId ? el.tourId === tourId : true)
        .filter((el) => limitYear ? el.date.split('-')[0] === limitYear : true);

      return {
        ...state,
        allShows: concerts.length
      };
    },
    getMostPlayedSongs (state, action) {
      const { tourId, albumId, limit, limitYear } = action.payload;

      const concerts = concertsData
          .filter((el) => tourId ? el.tourId === tourId : true)
          .filter((el) => limitYear ? el.date.split('-')[0] === limitYear : true);

      const allConcertsSongs = [];
        concerts.forEach((el) => {
          allConcertsSongs.push(...el.songList);
      });
      const onlySongs = allConcertsSongs.filter((el) => el !== 'opening' && el !== 'intro' && el !== 'instrumental' && el !== 'encore' && el !== 'encore2');

      const allSongMetadata = onlySongs.reduce((acc: MostPlayed[], current: string) => {
        const hasSongIndex = acc.findIndex((song) => song.id === current);

        if (hasSongIndex !== -1) {
          acc[hasSongIndex].number = ++acc[hasSongIndex].number;
        } else {
          const details = songsData.filter((song) => current === song.id)?.[0];
          acc.push({ id: current, number: 1, albumId: details?.albumId, details })
        }
        return acc;
      }, []);

      const allSongMetadataFiltered = albumId ? allSongMetadata.filter((el: MostPlayed) => el.albumId === albumId) : allSongMetadata;

      const mostPlayedSongs = limit
        ? _orderBy(allSongMetadataFiltered, 'number', 'desc').slice(0, limit)
        : _orderBy(allSongMetadataFiltered, 'number', 'desc');

      return {
        ...state,
        mostPlayedSongs
      };
    },
    getMostPlayedAlbums (state, action) {
      const { tourId, limit, limitYear } = action.payload;

      const concerts = concertsData
        .filter((el) => tourId ? el.tourId === tourId : true)
        .filter((el) => limitYear ? el.date.split('-')[0] === limitYear : true);

      const allConcertsSongs = [];
      concerts.forEach((el) => {
        allConcertsSongs.push(...el.songList);
      });
      const onlySongs = allConcertsSongs.filter((el) => el !== 'opening' && el !== 'intro' && el !== 'instrumental' && el !== 'encore' && el !== 'encore2');

      const allAlbumsMetadata = onlySongs.reduce((acc: MostPlayed[], current: string) => {
        const songDetails = songsData.filter((song) => current === song.id)?.[0];
        const details = albumsData.filter((album) => album.id === songDetails?.albumId)?.[0];
        const hasAlbumIndex = acc.findIndex((song) => song.id === songDetails?.albumId);

        if (hasAlbumIndex !== -1) {
          acc[hasAlbumIndex].number = ++acc[hasAlbumIndex].number;
        } else {
          if (songDetails?.albumId) {
            acc.push({ id: songDetails.albumId, number: 1, details })
          }
        }
        return acc;
      }, []);

      const mostPlayedAlbums = limit
        ? _orderBy(allAlbumsMetadata, 'number', 'desc').slice(0, limit)
        : _orderBy(allAlbumsMetadata, 'number', 'desc');

      return {
        ...state,
        mostPlayedAlbums
      };
    },
    getSongPlayedCount (state, action) {
      const { songId } = action.payload;

      const allConcertsSongs = [];
      concertsData.forEach((el) => {
        allConcertsSongs.push(...el.songList);
      });

      const onlySongId = allConcertsSongs.filter((el) => el === songId);

      return {
        ...state,
        songPlayed: onlySongId.length
      };
    },
    getAlbumPlayedCount (state, action) {
      const { albumId } = action.payload;

      const allConcertsSongs = [];
      concertsData.forEach((el) => {
        allConcertsSongs.push(...el.songList);
      });
      const onlySongs = allConcertsSongs.filter((el) => el !== 'opening' && el !== 'intro' && el !== 'instrumental' && el !== 'encore');

      const allCounts = onlySongs.reduce((acc: number, current: string) => {
        const songDetails = songsData.filter((song) => current === song.id)?.[0];
        const albumDetails = albumsData.filter((album) => album.id === songDetails?.albumId)?.[0];

        return albumDetails?.id === albumId ? ++acc : acc;
      }, 0);

      return {
        ...state,
        albumPlayed: allCounts
      };
    },
    getBasicBandStats (state) {
      const songs =  songsData.length;
      const albums =  albumsData.filter((album) => album.typeId === 'album' && album.international === false)?.length;
      const internationalAlbums =  albumsData.filter((album) => album.typeId === 'album' && album.international)?.length;
      const ep =  albumsData.filter((album) => album.typeId === 'miniAlbum')?.length;

      return {
        ...state,
        bandStats: {
          ...state.bandStats,
          songs,
          albums,
          internationalAlbums,
          ep
        }
      };
    },
    getYearsOnTour (state) {
      const years = concertsData.reduce((acc: Years, current: Concert) => {
        const currentYear = current.date.split('-')?.[0];
        const hasYear = (acc?.[currentYear]?.length || 0) > 0;
        return { ...acc, [currentYear]: hasYear ? [ ...acc?.[currentYear], current ] : [ current ] };
      }, {});

      const yearsFes = concertsData.reduce((acc: Years, current: Concert) => {
        const currentYear = current.date.split('-')?.[0];
        const hasYear = (acc?.[currentYear]?.length || 0) > 0;
        return current.tourId === 'festivals-shows-special-events'
          ? { ...acc, [currentYear]: hasYear ? [ ...acc?.[currentYear], current ] : [ current ] }
          : acc;
      }, {});

      return {
        ...state,
        years,
        yearsFes
      };
    }
  }
});

export const {
  getAllShows,
  getMostPlayedSongs,
  getMostPlayedAlbums,
  getSongPlayedCount,
  getAlbumPlayedCount,
  getBasicBandStats,
  getYearsOnTour
} = statisticsSlice.actions;

export default statisticsSlice.reducer;
